/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { CommonApi } from '../../services/api/common';

/**
 * Catches render errors and failed lazy() chunk loads in the tree.
 * When an error occurs, sends an immediate email to support@kaimakiweb.com with page and details.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  componentDidMount() {
    sessionStorage.removeItem('chunk_reload_attempted');
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (error?.name === 'ChunkLoadError') {
      const key = 'chunk_reload_attempted';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return;
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
    const page = this.props.pathname || window?.location?.pathname || '';
    const username = this.props.username ?? '';
    const browser = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
    CommonApi.reportFrontendError({
      page,
      message: error?.message || String(error),
      stack: error?.stack,
      component_stack: errorInfo?.componentStack,
      username,
      browser,
    });
  }

  render() {
    const { children, t } = this.props;
    const { hasError, error } = this.state;

    if (hasError) {
      const message = error?.message;
      return (
        <div
          style={{
            textAlign: 'center',
            padding: 24,
            fontSize: 18,
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>{t('common.errorBoundary')}</p>
          {process.env.NODE_ENV === 'development' && message && (
            <p style={{ marginTop: 8, fontSize: 14, color: '#666', wordBreak: 'break-word', maxWidth: 560 }}>
              {message}
            </p>
          )}
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  pathname: PropTypes.string,
  username: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ErrorBoundary);
